import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../utils/api';
import { WARN_MS, idleLimitMs, recordActivity, clearSession, expireSession } from '../utils/session';

const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];

// Idle-session watcher: warns before expiry and sends the user to login when the session ends
export default function SessionTimer() {
  const location = useLocation();
  const [warning, setWarning] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);

  useEffect(() => {
    if (!localStorage.getItem('token')) return undefined;
    // Re-run on route change so the timer starts right after login
    if (!localStorage.getItem('lastActivity')) localStorage.setItem('lastActivity', String(Date.now())); // server set lastActiveAt at login

    let timer;
    const endSession = (reason) => {
      clearInterval(timer); // stop ticking so a later check cannot overwrite the redirect
      clearSession();
      window.location.href = reason ? `/login?reason=${reason}` : '/login';
    };

    const tick = () => {
      const now = Date.now();
      const role = JSON.parse(localStorage.getItem('user') || 'null')?.role;
      if (!localStorage.getItem('token')) return endSession(null); // logged out in another tab
      const remaining = Number(localStorage.getItem('lastActivity')) + idleLimitMs(role) - now;
      if (remaining <= 0) {
        clearInterval(timer); // stop ticking so a later check cannot overwrite the notice redirect
        return expireSession('idle');
      }
      setWarning(remaining <= WARN_MS);
      setSecondsLeft(Math.ceil(remaining / 1000));
    };

    const onActivity = () => recordActivity();
    const onStorage = (e) => { if (e.key === 'token' && !e.newValue) window.location.href = '/login'; };

    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    window.addEventListener('storage', onStorage);
    timer = setInterval(tick, 1000);
    tick();
    return () => {
      clearInterval(timer);
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity));
      window.removeEventListener('storage', onStorage);
    };
  }, [location.pathname]);

  if (!warning) return null;

  const stay = () => {
    recordActivity(true);
    setWarning(false);
  };
  const logout = () => {
    api.post('/api/users/logout').catch(() => {}).finally(() => {
      clearSession();
      window.location.href = '/login';
    });
  };

  const mins = Math.floor(secondsLeft / 60);
  const secs = String(secondsLeft % 60).padStart(2, '0');

  return (
    <div role="alertdialog" aria-modal="true" aria-label="Session expiring" className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className="bg-white text-gray-800 rounded-lg shadow-xl p-6 max-w-sm w-full text-center">
        <h2 className="text-lg font-semibold mb-2">Still there?</h2>
        <p className="text-sm mb-4">
          You will be signed out for inactivity in <strong>{mins}:{secs}</strong>. Save any unsubmitted changes first.
        </p>
        <div className="flex gap-2 justify-center">
          <button type="button" onClick={logout} className="px-4 py-2 rounded bg-gray-200">Log out</button>
          <button type="button" onClick={stay} className="px-4 py-2 rounded bg-green-600 text-white">Stay signed in</button>
        </div>
      </div>
    </div>
  );
}
